/**
 * Compact candidate metrics — the small subset of {@link CalendarAnalysis} the
 * candidates comparison table renders. Extracted so every producer emits the SAME
 * shape/keys:
 *   - the generator modal (fresh + reattached optimize, and simulate),
 *   - the Job Center recovery path (client fallback for pre-v21 jobs),
 *   - the draw ceremony, when it closes and writes its candidate,
 *   - the container (Python port in calendar-optimizer/container/app/analytics.py,
 *     which is byte-compatible with this — see its golden test).
 *
 * Keep the key set in lockstep with compute_candidate_metrics() on the container.
 *
 * A candidate whose row shows no numbers is not merely untidy: the whole hub is
 * a comparison screen, and a blank row cannot be weighed against a filled one.
 * `deriveCompactCandidateMetrics` exists so a producer that failed to emit them
 * — every draw candidate written before this was wired up — can still be
 * measured from the pairings it did store.
 */

import { analyzeCalendar, type AnalyzePairing, type CalendarAnalysis } from "./analyzeCalendar";
import type { LatLng } from "../geo/haversine";

export interface CompactCandidateMetrics {
  maxAwayStreak: number;
  maxHomeStreak: number;
  totalBreaks: number;
  /** Break fairness: worst single team + spread (max−min) across teams. Lower = fairer. */
  breaksMaxPerTeam: number;
  breaksSpread: number;
  travelMax: number | null;
  travelStdev: number | null;
  /** "withCoords/total" e.g. "6/8" — how many teams had stadium coordinates. */
  coverage: string;
}

/** Derive the compact table metrics from a full CalendarAnalysis. */
export function compactCandidateMetrics(
  analysis: CalendarAnalysis,
): CompactCandidateMetrics {
  const breaks = analysis.teams.map((t) => t.breaks);
  return {
    maxAwayStreak: analysis.homeAway.maxAwayStreak,
    maxHomeStreak: analysis.homeAway.maxHomeStreak,
    totalBreaks: analysis.homeAway.totalBreaks,
    breaksMaxPerTeam: breaks.length ? Math.max(...breaks) : 0,
    breaksSpread: breaks.length ? Math.max(...breaks) - Math.min(...breaks) : 0,
    travelMax: analysis.distance.max,
    travelStdev: analysis.distance.stdev,
    coverage: `${analysis.distance.coverage.teamsWithCoords}/${analysis.distance.coverage.teamsTotal}`,
  };
}

/**
 * Whether a stored `metrics` blob already carries the comparison numbers.
 *
 * Keyed on the two the hub row renders unconditionally rather than on the whole
 * set: `travelMax`/`travelStdev` are legitimately null for a group with no
 * stadium coordinates, so requiring them would send a perfectly measured
 * candidate back through the derivation for nothing.
 */
export function hasCompactCandidateMetrics(metrics: unknown): boolean {
  const m = (metrics ?? {}) as Record<string, unknown>;
  return typeof m.totalBreaks === "number" && typeof m.maxAwayStreak === "number";
}

/**
 * Measure a fixture list that arrived without metrics.
 *
 * `legs` and `roundsPerLeg` are inferred from the pairings when the caller has
 * no generation result to read them off — a stored candidate is just rows, and
 * guessing them wrong would only mislabel the analysis metadata, never the
 * breaks or the travel.
 */
export function deriveCompactCandidateMetrics(
  pairings: readonly AnalyzePairing[],
  coordsByTeamId: Map<string, LatLng | null>,
  wilayaByTeamId: Map<string, string | null>,
  opts: { legs?: number; roundsPerLeg?: number } = {},
): CompactCandidateMetrics {
  const legs = opts.legs ?? Math.max(1, ...pairings.map((p) => p.leg || 1));
  const totalRounds = Math.max(0, ...pairings.map((p) => p.game_day || 0));
  const roundsPerLeg = opts.roundsPerLeg ?? Math.round(totalRounds / Math.max(1, legs));
  return compactCandidateMetrics(
    analyzeCalendar([...pairings], coordsByTeamId, wilayaByTeamId, { legs, roundsPerLeg }),
  );
}
